import { useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db/db';
import { useAthleteStore } from '@/store';
import {
    startOfWeek, startOfMonth, subDays, format,
    differenceInCalendarDays, isAfter, parseISO
} from 'date-fns';

/* ─── Achievement Definitions ────────────────────────────────── */
export interface Achievement {
    id: string;
    name: string;
    description: string;
    icon: string; // emoji
    condition: (stats: TrainingStats) => boolean;
}

export const ACHIEVEMENTS: Achievement[] = [
    // Session milestones
    { id: 'first_session', name: 'First Step', description: 'Complete your first session', icon: '👣', condition: s => s.totalSessions >= 1 },
    { id: '10_sessions', name: 'Getting Serious', description: 'Complete 10 sessions', icon: '💪', condition: s => s.totalSessions >= 10 },
    { id: '25_sessions', name: 'Dedicated', description: 'Complete 25 sessions', icon: '🔥', condition: s => s.totalSessions >= 25 },
    { id: '50_sessions', name: 'Warrior', description: 'Complete 50 sessions', icon: '⚔️', condition: s => s.totalSessions >= 50 },
    { id: '100_sessions', name: 'Centurion', description: 'Complete 100 sessions', icon: '🏛️', condition: s => s.totalSessions >= 100 },

    // Streak milestones
    { id: 'streak_3', name: 'Hat Trick', description: '3-day training streak', icon: '🎩', condition: s => s.currentStreak >= 3 },
    { id: 'streak_7', name: 'Iron Week', description: '7-day training streak', icon: '🗓️', condition: s => s.currentStreak >= 7 },
    { id: 'streak_14', name: 'Unstoppable', description: '14-day training streak', icon: '⚡', condition: s => s.currentStreak >= 14 },
    { id: 'streak_30', name: 'Legendary', description: '30-day training streak', icon: '👑', condition: s => s.currentStreak >= 30 },

    // Volume milestones (total minutes)
    { id: '60_min', name: 'First Hour', description: 'Train for a total of 60 minutes', icon: '⏱️', condition: s => s.totalMinutes >= 60 },
    { id: '300_min', name: 'Five Hours Deep', description: 'Train for a total of 5 hours', icon: '🕐', condition: s => s.totalMinutes >= 300 },
    { id: '600_min', name: 'Ten Hour Club', description: 'Train for a total of 10 hours', icon: '🏆', condition: s => s.totalMinutes >= 600 },

    // Rounds milestones
    { id: '100_rounds', name: 'Century Rounds', description: 'Complete 100 total rounds', icon: '🔄', condition: s => s.totalRounds >= 100 },
    { id: '500_rounds', name: 'Round Machine', description: 'Complete 500 total rounds', icon: '🤖', condition: s => s.totalRounds >= 500 },

    // Position coverage
    { id: 'all_positions', name: 'Complete Wrestler', description: 'Train all three positions', icon: '🤼', condition: s => s.positionBreakdown.neutral > 0 && s.positionBreakdown.top > 0 && s.positionBreakdown.bottom > 0 },
];

/* ─── Stats Interface ────────────────────────────────────────── */
export interface TrainingStats {
    totalSessions: number;
    totalMinutes: number;
    totalRounds: number;
    currentStreak: number;
    longestStreak: number;
    weeklyMinutes: number;
    monthlyMinutes: number;
    weeklySessions: number;
    monthlySessions: number;
    positionBreakdown: { neutral: number; top: number; bottom: number };
    dailyActivity: { date: string; minutes: number; sessions: number }[];
    weeklyTrend: { week: string; minutes: number; sessions: number }[];
    unlockedAchievements: Achievement[];
    lockedAchievements: Achievement[];
    personalRecords: {
        longestSession: number; // seconds
        mostRoundsInSession: number;
        bestWeek: number; // minutes
    };
}

/* ─── Hook ───────────────────────────────────────────────────── */
export function useTrainingStats(): TrainingStats {
    const activeId = useAthleteStore(s => s.activeId);

    const sessions = useLiveQuery(
        () => db.trainingSessions.where('athleteId').equals(activeId).toArray(),
        [activeId]
    ) ?? [];

    return useMemo(() => {
        const now = new Date();
        const weekStart = startOfWeek(now, { weekStartsOn: 1 });
        const monthStart = startOfMonth(now);

        // Basic totals
        const totalSessions = sessions.length;
        const totalMinutes = sessions.reduce((s, x) => s + Math.round(x.totalSeconds / 60), 0);
        const totalRounds = sessions.reduce((s, x) => s + x.completedRounds, 0);

        // Weekly / Monthly
        const weeklyS = sessions.filter(s => isAfter(parseISO(s.startedAt), weekStart));
        const monthlyS = sessions.filter(s => isAfter(parseISO(s.startedAt), monthStart));
        const weeklyMinutes = weeklyS.reduce((s, x) => s + Math.round(x.totalSeconds / 60), 0);
        const monthlyMinutes = monthlyS.reduce((s, x) => s + Math.round(x.totalSeconds / 60), 0);

        // Streak calculation
        const sessionDates = new Set(sessions.map(s => format(parseISO(s.startedAt), 'yyyy-MM-dd')));
        let currentStreak = 0;
        let check = now;
        // Check today first
        if (!sessionDates.has(format(check, 'yyyy-MM-dd'))) {
            check = subDays(check, 1);
        }
        while (sessionDates.has(format(check, 'yyyy-MM-dd'))) {
            currentStreak++;
            check = subDays(check, 1);
        }

        // Longest streak
        const sortedDates = [...sessionDates].sort();
        let longestStreak = 0;
        let tempStreak = 1;
        for (let i = 1; i < sortedDates.length; i++) {
            const prev = sortedDates[i - 1];
            const curr = sortedDates[i];
            if (!prev || !curr) continue;
            const diff = differenceInCalendarDays(parseISO(curr), parseISO(prev));
            if (diff === 1) {
                tempStreak++;
            } else {
                longestStreak = Math.max(longestStreak, tempStreak);
                tempStreak = 1;
            }
        }
        longestStreak = Math.max(longestStreak, tempStreak, currentStreak);
        if (sortedDates.length === 0) longestStreak = 0;

        // Position breakdown (in minutes)
        const positionBreakdown = { neutral: 0, top: 0, bottom: 0 };
        sessions.forEach(s => {
            const mins = Math.round(s.totalSeconds / 60);
            if (s.position === 'neutral') positionBreakdown.neutral += mins;
            else if (s.position === 'top') positionBreakdown.top += mins;
            else if (s.position === 'bottom') positionBreakdown.bottom += mins;
        });

        // Daily activity (last 30 days)
        const dailyActivity: TrainingStats['dailyActivity'] = [];
        for (let i = 29; i >= 0; i--) {
            const d = subDays(now, i);
            const key = format(d, 'yyyy-MM-dd');
            const daySessions = sessions.filter(s => format(parseISO(s.startedAt), 'yyyy-MM-dd') === key);
            dailyActivity.push({
                date: key,
                minutes: daySessions.reduce((sum, x) => sum + Math.round(x.totalSeconds / 60), 0),
                sessions: daySessions.length,
            });
        }

        // Weekly trend (last 8 weeks)
        const weeklyTrend: TrainingStats['weeklyTrend'] = [];
        for (let i = 7; i >= 0; i--) {
            const ws = startOfWeek(subDays(now, i * 7), { weekStartsOn: 1 });
            const we = subDays(startOfWeek(subDays(now, (i - 1) * 7), { weekStartsOn: 1 }), 0);
            const label = format(ws, 'MMM d');
            const wSessions = sessions.filter(s => {
                const d = parseISO(s.startedAt);
                return isAfter(d, ws) && !isAfter(d, we);
            });
            weeklyTrend.push({
                week: label,
                minutes: wSessions.reduce((sum, x) => sum + Math.round(x.totalSeconds / 60), 0),
                sessions: wSessions.length,
            });
        }

        // Personal records
        const personalRecords = {
            longestSession: sessions.reduce((max, s) => Math.max(max, s.totalSeconds), 0),
            mostRoundsInSession: sessions.reduce((max, s) => Math.max(max, s.completedRounds), 0),
            bestWeek: weeklyTrend.reduce((max, w) => Math.max(max, w.minutes), 0),
        };

        // Achievements
        const stats: TrainingStats = {
            totalSessions,
            totalMinutes,
            totalRounds,
            currentStreak,
            longestStreak,
            weeklyMinutes,
            monthlyMinutes,
            weeklySessions: weeklyS.length,
            monthlySessions: monthlyS.length,
            positionBreakdown,
            dailyActivity,
            weeklyTrend,
            personalRecords,
            unlockedAchievements: [],
            lockedAchievements: [],
        };

        stats.unlockedAchievements = ACHIEVEMENTS.filter(a => a.condition(stats));
        stats.lockedAchievements = ACHIEVEMENTS.filter(a => !a.condition(stats));

        return stats;
    }, [sessions]);
}
